/** Shared bounded primitives. These guards inspect untrusted values without coercing them. */
export function isTuningRecord(value: unknown): value is Record<string, unknown> {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

export function isTuningString(value: unknown, maximum = 128): value is string {
  return typeof value === 'string' && value['length'] > 0 && value['length'] <= maximum && !value['includes']('\0');
}

export function isTuningNumber(value: unknown, minimum = 0, maximum = Number.MAX_SAFE_INTEGER): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value >= minimum && value <= maximum;
}

export function isTuningInteger(value: unknown, minimum = 0, maximum = Number.MAX_SAFE_INTEGER): value is number {
  return isTuningNumber(value, minimum, maximum) && Number.isSafeInteger(value);
}

export function hasOnlyTuningKeys(value: Record<string, unknown>, keys: readonly string[]): boolean {
  return Object.keys(value).every((key) => keys.includes(key));
}

export function isTuningEnum(value: unknown, values: readonly string[]): value is string {
  return typeof value === 'string' && values.includes(value);
}

export function isTuningError(value: unknown): value is { code: string; message: string } {
  return isTuningRecord(value) && hasOnlyTuningKeys(value, ['code', 'message']) &&
    isTuningString(value['code']) && isTuningString(value['message'], 2_048);
}

/** Returns infinity for unserializable input. Byte limits include UTF-8, not just characters. */
export function tuningJsonBytes(value: unknown): number {
  try {
    const json = JSON.stringify(value);
    return typeof json === 'string' ? new TextEncoder().encode(json).byteLength : Infinity;
  } catch {
    return Infinity;
  }
}

export function isTuningArray(value: unknown, maximum: number, guard: (item: unknown) => boolean): value is unknown[] {
  return Array.isArray(value) && value['length'] <= maximum && value['every'](guard);
}

export function isOptionalTuningNumber(value: unknown, minimum = 0): boolean {
  return value === undefined || isTuningNumber(value, minimum);
}
